
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { WebPage } from '@/types/masterLicenseSettings';
import { ExternalLink, FileText, Plus, Edit, Trash2, Eye } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const WebPagesTab: React.FC = () => {
  // In a real application, this would come from an API
  const [webPages, setWebPages] = useState<WebPage[]>([
    {
      id: '1',
      title: 'Home Page',
      url: '/index.html',
      lastUpdated: '2025-04-20',
      status: 'live',
    },
    {
      id: '2',
      title: 'About Us',
      url: '/about.html',
      lastUpdated: '2025-04-18',
      status: 'live',
    },
    {
      id: '3',
      title: 'Services',
      url: '/services.html',
      lastUpdated: '2025-04-15',
      status: 'live',
    },
    {
      id: '4',
      title: 'Blog',
      url: '/blog/index.html',
      lastUpdated: '2025-04-22',
      status: 'live',
    },
    {
      id: '5',
      title: 'Contact',
      url: '/contact.html',
      lastUpdated: '2025-04-10',
      status: 'live',
    },
    {
      id: '6',
      title: 'New Pricing Page',
      url: '/pricing-v2.html',
      lastUpdated: '2025-04-24',
      status: 'draft',
    },
    {
      id: '7',
      title: 'Old Testimonials',
      url: '/testimonials-old.html',
      lastUpdated: '2024-12-15',
      status: 'archived',
    },
    {
      id: '8',
      title: 'SaaS Landing Page',
      url: '/saas-landing.html',
      lastUpdated: '2025-04-26',
      status: 'live',
    },
    {
      id: '9',
      title: 'Features Overview',
      url: '/features.html',
      lastUpdated: '2025-04-25',
      status: 'live',
    },
    {
      id: '10',
      title: 'Reseller Program',
      url: '/reseller.html',
      lastUpdated: '2025-04-23',
      status: 'live',
    },
    {
      id: '11',
      title: 'Knowledge Base',
      url: '/kb/index.html',
      lastUpdated: '2025-04-21',
      status: 'live',
    },
    {
      id: '12',
      title: 'Partners Portal',
      url: '/partners/index.html',
      lastUpdated: '2025-04-19',
      status: 'live',
    }
  ]);

  const [editingPage, setEditingPage] = useState<WebPage | null>(null);
  const [previewPage, setPreviewPage] = useState<WebPage | null>(null);
  const { toast } = useToast();

  const getStatusBadgeClass = (status: string) => {
    switch(status) {
      case 'live':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const handleEditPage = (page: WebPage) => {
    setEditingPage({...page});
  };

  const handleSavePage = () => {
    if (editingPage) {
      setWebPages(webPages.map(page => page.id === editingPage.id ? editingPage : page));
      setEditingPage(null);
      toast({
        title: "Page Updated",
        description: `${editingPage.title} has been updated successfully.`
      });
    }
  };

  const handlePreviewPage = (page: WebPage) => {
    setPreviewPage(page);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Website Pages</CardTitle>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add New Page
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Page Title</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {webPages.map((page) => (
              <TableRow key={page.id}>
                <TableCell className="flex items-center">
                  <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                  {page.title}
                </TableCell>
                <TableCell>
                  <span className="text-blue-600">{page.url}</span>
                </TableCell>
                <TableCell>{page.lastUpdated}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(page.status)}`}>
                    {page.status.charAt(0).toUpperCase() + page.status.slice(1)}
                  </span>
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="icon" asChild>
                    <a href={`https://example.com${page.url}`} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      <span className="sr-only">View page</span>
                    </a>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleEditPage(page)}
                  >
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">Edit page</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handlePreviewPage(page)}
                  >
                    <Eye className="h-4 w-4" />
                    <span className="sr-only">Preview</span>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Edit Page Dialog */}
        <Dialog open={!!editingPage} onOpenChange={(open) => !open && setEditingPage(null)}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Edit Page: {editingPage?.title}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label htmlFor="title">Page Title</label>
                <Input
                  id="title"
                  value={editingPage?.title || ''}
                  onChange={(e) => setEditingPage(prev => prev ? {...prev, title: e.target.value} : prev)}
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="url">URL Path</label>
                <Input
                  id="url"
                  value={editingPage?.url || ''}
                  onChange={(e) => setEditingPage(prev => prev ? {...prev, url: e.target.value} : prev)}
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="status">Status</label>
                <Select 
                  value={editingPage?.status} 
                  onValueChange={(value) => setEditingPage(prev => prev ? {...prev, status: value as 'live' | 'draft' | 'archived'} : prev)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select page status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="live">Live</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <label htmlFor="content">Page Content (HTML)</label>
                <Textarea
                  id="content"
                  rows={10}
                  placeholder="Enter page HTML content"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingPage(null)}>Cancel</Button>
              <Button onClick={handleSavePage}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Page Dialog */}
        <Dialog open={!!previewPage} onOpenChange={(open) => !open && setPreviewPage(null)}>
          <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Previewing: {previewPage?.title}</DialogTitle>
            </DialogHeader>
            <div className="border rounded-md p-4 bg-white">
              <div className="flex justify-between items-center mb-4 pb-2 border-b">
                <span className="font-medium text-sm text-gray-500">
                  {`Preview of ${previewPage?.url}`}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs ${previewPage && getStatusBadgeClass(previewPage.status)}`}>
                  {previewPage?.status.charAt(0).toUpperCase() + previewPage?.status.slice(1)}
                </span>
              </div>
              <div className="prose max-w-none">
                <h1>{previewPage?.title}</h1>
                <p>This is a preview of the {previewPage?.title} page content. In a real application, this would show the actual rendered content of the page.</p>
                <p>Last updated: {previewPage?.lastUpdated}</p>
                <div className="flex justify-end mt-4">
                  <Button variant="outline" size="sm" asChild>
                    <a href={`https://example.com${previewPage?.url}`} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Open in new tab
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default WebPagesTab;
